/**
 * Browser-first scheduling lease orchestration. The browser computes each bounded snapshot and
 * paints a provisional result before submitting only placements. Full activity records enter the
 * durable UI store only after server validation. Identity, mutation and abort fences are checked
 * at every asynchronous boundary; rejected proposals never trigger hidden server computation.
 * @module apps/web/src/lib/browserScheduleFlow
 */
import type { SchedulingPreview } from './schedulingProtocol';

/** Monotonic local logout-intent fence, independent of the slower network/session teardown. */
let schedulingSessionEpoch = 0;

/** Capture before preparing a snapshot so a logout cannot start a new Worker from a late reply. */
export function getSchedulingSessionEpoch(): number {
  return schedulingSessionEpoch;
}

/** Invalidate preparation, compute and save continuations synchronously when logout starts. */
export function invalidateSchedulingSession(): void {
  schedulingSessionEpoch += 1;
}

/** Authorized bounded batch; the lease is a freshness fence, not a client permission grant. */
export interface BrowserSchedulingLease {
  /** Job identity assigned by the authenticated server. */
  job_id: string;
  /** Canonical snapshot revision. */
  revision: number;
  /** Opaque token binding this specific snapshot to the proposed placements. */
  lease_id: string;
  /** Server expiry timestamp; authoritative validation always checks it again. */
  lease_expires_at: string;
  /** Immutable authorized canonical solver input. */
  snapshot: unknown;
}

/** Scheduling status returned at start and after each accepted batch. */
export interface BrowserScheduleStatus {
  /** Stable job identity across every batch. */
  job_id: string;
  /** Scope selected at job creation; rollover is limited to missed activities. */
  purpose?: 'rollover' | 'full';
  /** Server lifecycle state. */
  status: string;
  /** Number of activities included in the whole job. */
  total?: number;
  /** Newly scheduled activities so far. */
  scheduled?: number;
  /** Routine activities optimized so far. */
  optimized?: number;
  /** Activities left unplaced so far. */
  failed?: number;
  /** Activities still awaiting the next batch. */
  pending?: number;
  /** Human-readable server progress message. */
  message?: string;
  /** Next bounded snapshot, absent when the job has finished. */
  browser?: BrowserSchedulingLease;
  /** Authoritative full records committed in the preceding proposal. */
  changes?: unknown[];
}

/** Bounded scheduling-only write; arbitrary activity fields are never accepted from the browser. */
export interface BrowserScheduleProposal {
  /** Job to which the captured lease belongs. */
  job_id: string;
  /** Exact revision computed locally. */
  revision: number;
  /** Snapshot lease checked by the server before commit. */
  lease_id: string;
  /** Local optimizer's proposed intervals for selected activities. */
  placements: Array<{
    /** Selected activity identity. */
    task_id: string;
    /** Ordered non-overlapping ISO timestamp intervals. */
    chunks: Array<{
      /** Inclusive interval start. */ start: string;
      /** Exclusive interval end. */ end: string;
    }>;
  }>;
  /** Selected activities for which the optimizer found no placement. */
  unplaced_ids: string[];
}

/** Callbacks keep transport, private session state and React rendering outside the pure loop. */
export interface BrowserScheduleFlowOptions {
  /** First authenticated response, including its first lease when work remains. */
  initial: BrowserScheduleStatus;
  /** Cancellation immediately fences late Worker or network replies. */
  signal: AbortSignal;
  /** Throw if owner, token, task revision or component lifetime changed. */
  assertCurrent: () => void;
  /** Compute exactly the captured snapshot in the persistent browser Worker. */
  solve: (
    snapshot: unknown,
    signal: AbortSignal
  ) => Promise<{
    /** Canonical staged documents. */ result: SchedulingPreview;
    /** Local compute only, excluding transport. */ computeMs: number;
  }>;
  /** Strip canonical documents down to the supported scheduling-only proposal fields. */
  proposal: (
    result: SchedulingPreview
  ) => Pick<BrowserScheduleProposal, 'placements' | 'unplaced_ids'>;
  /** Authenticate and validate the proposed intervals; success may return another lease. */
  submit: (
    proposal: BrowserScheduleProposal,
    signal: AbortSignal
  ) => Promise<BrowserScheduleStatus>;
  /** Display tentative data separately from canonical records. */
  preview: (result: SchedulingPreview, computeMs: number) => void;
  /** Apply authoritative committed documents in one store update and renew revision fence. */
  accepted: (status: BrowserScheduleStatus) => void;
  /** Clear tentative data on completion, failure or cancellation. */
  clearPreview: () => void;
}

/** Run bounded local batches with provenance checks and no implicit server fallback. */
export async function runBrowserScheduleFlow(
  options: BrowserScheduleFlowOptions
): Promise<BrowserScheduleStatus> {
  const jobId = options.initial.job_id;
  let status = options.initial;
  const leases = new Set<string>();
  /** Reject cancellation before inspecting or displaying any asynchronous result. */
  const fence = () => {
    options.signal.throwIfAborted();
    options.assertCurrent();
  };
  try {
    while (status.browser) {
      fence();
      const lease = status.browser;
      if (status.job_id !== jobId || lease.job_id !== jobId || leases.has(lease.lease_id))
        throw new Error(
          'This scheduling batch was superseded. Solve again with the current calendar.'
        );
      leases.add(lease.lease_id);
      const { result, computeMs } = await options.solve(lease.snapshot, options.signal);
      fence();
      if (result.job_id !== jobId || result.revision !== lease.revision)
        throw new Error('The browser result belongs to an expired scheduling snapshot.');
      options.preview(result, computeMs);
      // Preview state can commit to React while the credentialed proposal is in flight.
      status = await options.submit(
        {
          job_id: jobId,
          revision: lease.revision,
          lease_id: lease.lease_id,
          ...options.proposal(result),
        },
        options.signal
      );
      options.signal.throwIfAborted();
      // accepted owns the post-commit fence: a concurrent realtime commit may have advanced
      // the local revision even though this proposal itself was safely committed on the server.
      if (status.job_id !== jobId) throw new Error('The scheduling job was superseded.');
      options.accepted(status);
      options.clearPreview();
    }
    if ((status.pending ?? 0) > 0)
      throw new Error(
        status.message || 'The local scheduling lease expired. Solve again to resume.'
      );
    return status;
  } finally {
    options.clearPreview();
  }
}
